import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';
import { getBoardSize } from '../../utils/gameLayout';

const LEVELS = {
  neurona: { nombre: 'Neurona', icono: '🧠', secuencia_max: 4, colores: 4, velocidad: 800 },
  sinapsis: { nombre: 'Sinapsis', icono: '⚡', secuencia_max: 6, colores: 4, velocidad: 700 },
  cerebro: { nombre: 'Cerebro', icono: '🧩', secuencia_max: 8, colores: 6, velocidad: 600 },
  genio: { nombre: 'Genio', icono: '💡', secuencia_max: 10, colores: 6, velocidad: 500 },
  einstein: { nombre: 'Einstein', icono: '🎓', secuencia_max: 12, colores: 8, velocidad: 400 }
};

const BUTTON_EMOJIS = ['🔴', '🔵', '🟡', '🟢', '🟣', '🟠', '🟤', '⚫'];
const BUTTON_COLORS = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#A8E6CF', '#FFD3B6', '#FFAAA5', '#FF8B94'];

export default function SimonGame() {
  const tenant = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const nivel = (searchParams.get('nivel') as keyof typeof LEVELS) || 'neurona';
  const config = LEVELS[nivel];

  const [sequence, setSequence] = useState<number[]>([]);
  const [playerSequence, setPlayerSequence] = useState<number[]>([]);
  const [round, setRound] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameActive, setGameActive] = useState(false);
  const [playingSequence, setPlayingSequence] = useState(false);
  const [activeButton, setActiveButton] = useState<number | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [gameResult, setGameResult] = useState<'victory' | 'defeat' | null>(null);
  const [message, setMessage] = useState('Presiona Iniciar para jugar');

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameActive) {
      interval = setInterval(() => setSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [gameActive]);

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const lightButton = useCallback(async (colorIndex: number) => {
    setActiveButton(colorIndex);
    await sleep(config.velocidad / 2);
    setActiveButton(null);
    await sleep(100);
  }, [config.velocidad]);

  const playSequenceAnimation = useCallback(async (seq: number[]) => {
    setPlayingSequence(true);
    setMessage('¡Observa la secuencia!');

    for (const color of seq) {
      await sleep(config.velocidad);
      await lightButton(color);
    }

    setPlayingSequence(false);
    setMessage('¡Tu turno! Repite la secuencia');
  }, [config.velocidad, lightButton]);

  const startGame = async () => {
    setSequence([]);
    setPlayerSequence([]);
    setRound(0);
    setLives(3);
    setSeconds(0);
    setGameActive(true);
    setGameResult(null);
    setMessage('¡Prepárate!');

    await sleep(1000);
    nextRound([]);
  };

  const nextRound = async (currentSeq: number[]) => {
    const newRound = currentSeq.length + 1;

    if (newRound > config.secuencia_max) {
      handleVictory(newRound - 1);
      return;
    }

    const newColor = Math.floor(Math.random() * config.colores);
    const newSeq = [...currentSeq, newColor];

    setRound(newRound);
    setSequence(newSeq);
    setPlayerSequence([]);

    await sleep(500);
    await playSequenceAnimation(newSeq);
  };

  const handlePlayerInput = async (colorIndex: number) => {
    if (!gameActive || playingSequence) return;

    await lightButton(colorIndex);

    const newPlayerSeq = [...playerSequence, colorIndex];
    setPlayerSequence(newPlayerSeq);

    const currentIndex = newPlayerSeq.length - 1;

    if (newPlayerSeq[currentIndex] !== sequence[currentIndex]) {
      const newLives = lives - 1;
      setLives(newLives);
      setMessage('¡Error! 😢');

      if (newLives === 0) {
        handleDefeat();
      } else {
        setPlayerSequence([]);
        await sleep(1000);
        await playSequenceAnimation(sequence);
      }
    } else if (newPlayerSeq.length === sequence.length) {
      setMessage('¡Correcto! 🎉');
      await sleep(1000);
      nextRound(sequence);
    }
  };

  const handleVictory = async (finalRound: number) => {
    setGameActive(false);
    setGameResult('victory');

    if (finalRound > bestScore) {
      setBestScore(finalRound);
    }

    if (!selectedKid) return;

    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'simon',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: seconds,
        movimientos: finalRound
      });

      const minutosJugados = Math.ceil(seconds / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  const handleDefeat = async () => {
    setGameActive(false);
    setGameResult('defeat');

    if (round > bestScore) {
      setBestScore(round);
    }

    if (!selectedKid) return;

    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'simon',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: seconds,
        movimientos: round
      });

      const minutosJugados = Math.ceil(seconds / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${String(mins).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎵 Simón Dice</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setSearchParams({ nivel: key })}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          R{round} · Mejor: {bestScore} · {formatTime(seconds)}
        </span>
      </div>

      {/* Board */}
      <div className="flex-1 flex flex-col items-center justify-center p-3 gap-4">
        {/* Message + lives */}
        <div className="text-center">
          <p className="text-xl font-bold text-white drop-shadow">{message}</p>
          <div className="flex justify-center gap-2 mt-2">
            {[0, 1, 2].map(i => (
              <span key={i} className={`text-3xl ${i >= lives ? 'opacity-30' : ''}`}>❤️</span>
            ))}
          </div>
        </div>

        {/* Buttons grid */}
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns:
              config.colores === 4
                ? 'repeat(2, 1fr)'
                : config.colores === 6
                ? 'repeat(3, 1fr)'
                : 'repeat(4, 1fr)',
            width: getBoardSize(100)
          }}
        >
          {Array.from({ length: config.colores }).map((_, i) => (
            <button
              key={i}
              onClick={() => handlePlayerInput(i)}
              disabled={!gameActive || playingSequence}
              className={`aspect-square rounded-2xl text-6xl font-bold shadow-lg transition-all ${
                activeButton === i ? 'scale-110' : ''
              } ${!gameActive || playingSequence ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105'}`}
              style={{
                background: BUTTON_COLORS[i],
                filter: activeButton === i ? 'brightness(1.5)' : 'none',
                boxShadow: activeButton === i ? `0 0 30px ${BUTTON_COLORS[i]}` : undefined
              }}
            >
              {BUTTON_EMOJIS[i]}
            </button>
          ))}
        </div>

        {/* Start/reset button */}
        {!gameActive ? (
          <button
            onClick={startGame}
            className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
            style={{ background: tenant.theme.colors.primary }}
          >
            🎮 {gameResult ? 'Jugar de Nuevo' : 'Iniciar Juego'}
          </button>
        ) : (
          <button
            onClick={() => {
              setGameActive(false);
              setMessage('Juego cancelado');
            }}
            className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg"
            style={{ background: tenant.theme.colors.accent, color: '#333' }}
          >
            🔄 Reiniciar
          </button>
        )}
      </div>

      {/* Modal de Victoria */}
      {gameResult === 'victory' && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 text-center max-w-md animate-bounce-in">
            <div className="text-8xl mb-4">🎉</div>
            <h2
              className="text-4xl font-bold mb-6"
              style={{ fontFamily: tenant.theme.fonts.primary.family, color: tenant.theme.colors.primary }}
            >
              ¡Increíble!
            </h2>
            <div className="text-lg text-gray-700 mb-6 space-y-2">
              <p>Completaste el nivel <strong>{config.icono} {config.nombre}</strong></p>
              <p>Llegaste a la ronda <strong>{round}</strong></p>
              <p>Tiempo: <strong>{formatTime(seconds)}</strong></p>
            </div>
            <button
              onClick={startGame}
              className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
              style={{ background: tenant.theme.colors.primary }}
            >
              🎮 Jugar de Nuevo
            </button>
          </div>
        </div>
      )}

      {/* Modal de Derrota */}
      {gameResult === 'defeat' && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 text-center max-w-md animate-bounce-in">
            <div className="text-8xl mb-4">😢</div>
            <h2
              className="text-4xl font-bold mb-6"
              style={{ fontFamily: tenant.theme.fonts.primary.family, color: tenant.theme.colors.primary }}
            >
              ¡Casi lo logras!
            </h2>
            <div className="text-lg text-gray-700 mb-6 space-y-2">
              <p>Llegaste a la ronda <strong>{round}</strong></p>
              <p>Tiempo: <strong>{formatTime(seconds)}</strong></p>
            </div>
            <button
              onClick={startGame}
              className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
              style={{ background: tenant.theme.colors.primary }}
            >
              🎮 Intentar de Nuevo
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.6s;
        }
      `}</style>
    </div>
  );
}
